// Customer names for the whole app (the redesign shows names, not ids) and the searchable customer
// picker that replaced typed org-id inputs. A super-admin's directory is GET /v1/admin/orgs; a
// billing principal (who cannot list orgs) gets the names the revenue report carries for the last
// three years (billed and not billed); anyone else has none, and the picker still
// accepts a typed id. Loaded once, on first use, and shared.
import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useContext, useEffect, useId, useMemo, useRef, useState } from "preact/hooks";
import { useApi, addMonths, currentMonth, type ApiClient } from "@nexus/ui";
import type { Session } from "../session/session";
import { customerName, looksLikeCustomerId, matchCustomers, mergeDirectory, type CustomerEntry } from "./customers";

type DirectoryStatus = "idle" | "loading" | "ready" | "error";

interface DirectoryServices {
  status: DirectoryStatus;
  entries: readonly CustomerEntry[];
  /** Start loading, if nothing has yet. */
  ensure(): void;
  /** Add names a screen learnt on its own (an org it fetched). */
  remember(entries: readonly CustomerEntry[]): void;
}

const DirectoryContext = createContext<DirectoryServices | null>(null);

async function loadDirectory(client: ApiClient, session: Session): Promise<CustomerEntry[]> {
  if (session.me.role === "super-admin") {
    const entries: CustomerEntry[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < 20; page++) {
      const result = await client.get("/v1/admin/orgs", { query: { limit: 500, ...(cursor ? { cursor } : {}) } });
      entries.push(...result.items.map((org) => ({ id: org.id, name: org.name })));
      if (!result.nextCursor) break;
      cursor = result.nextCursor;
    }
    return mergeDirectory(entries);
  }
  if (session.can("billing:read")) {
    const to = currentMonth(new Date());
    const from = addMonths(to, -35);
    const [byOrg, summary] = await Promise.all([
      client.get("/v1/admin/revenue/by-org", { query: { from, to } }).catch(() => null),
      client.get("/v1/admin/revenue/summary", { query: { from, to } }).catch(() => null),
    ]);
    const named = (byOrg?.items ?? []).map((item) => ({ id: item.key, name: item.label || item.key }));
    const notBilled = (summary?.notBilled ?? []).map((item) => ({ id: item.orgId, name: item.label || item.orgId }));
    return mergeDirectory(named, notBilled);
  }
  return [];
}

export function CustomerDirectoryProvider({ session, children }: { session: Session | null; children: ComponentChildren }) {
  const client = useApi();
  const [status, setStatus] = useState<DirectoryStatus>("idle");
  const [entries, setEntries] = useState<readonly CustomerEntry[]>([]);
  const started = useRef(false);
  const subject = session?.me.subject ?? null;
  const loadedFor = useRef(subject);

  // A different principal (sign-out, then sign-in as someone else) starts from nothing. Not on the
  // first render: children's effects run first and have already started the load.
  useEffect(() => {
    if (loadedFor.current === subject) return;
    loadedFor.current = subject;
    started.current = false;
    setStatus("idle");
    setEntries([]);
  }, [subject]);

  const services = useMemo<DirectoryServices>(
    () => ({
      status,
      entries,
      ensure() {
        if (started.current || !client || !session) return;
        started.current = true;
        setStatus("loading");
        loadDirectory(client, session)
          .then((loaded) => {
            setEntries((known) => mergeDirectory(loaded, known));
            setStatus("ready");
          })
          .catch(() => setStatus("error"));
      },
      remember(more) {
        setEntries((known) => mergeDirectory(more, known));
      },
    }),
    [status, entries, client, session],
  );

  return <DirectoryContext.Provider value={services}>{children}</DirectoryContext.Provider>;
}

/** The shared directory (loading it on first use). */
export function useCustomerDirectory(): DirectoryServices {
  const services = useContext(DirectoryContext);
  useEffect(() => {
    services?.ensure();
  }, [services]);
  return services ?? { status: "ready", entries: [], ensure() {}, remember() {} };
}

/** A customer's name from the directory, or its id until the name is known. */
export function useCustomerName(id: string | null | undefined): string {
  const { entries } = useCustomerDirectory();
  return id ? customerName(entries, id) : "";
}

export interface CustomerPickerProps {
  /** The control's id (the <Field> label points at it). */
  id: string;
  /** The chosen customer id, or "" for none. */
  value: string;
  onChange(id: string): void;
  /** Offer "All customers" (value "") as the first choice. */
  allowAll?: boolean | undefined;
  placeholder?: string | undefined;
  describedBy?: string | undefined;
  invalid?: boolean | undefined;
}

/**
 * A combobox: type part of a name or id, pick from the list (name, id secondary). Arrow keys move,
 * Enter picks, Esc closes. A typed id that is not in the directory can still be used as-is.
 */
export function CustomerPicker({ id, value, onChange, allowAll, placeholder, describedBy, invalid }: CustomerPickerProps) {
  const { entries, status } = useCustomerDirectory();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);

  const selectedLabel = value === "" ? (allowAll ? "All customers" : "") : customerName(entries, value);
  const matches = matchCustomers(entries, query, 50);
  const typed = query.trim();
  const offerTyped = typed !== "" && looksLikeCustomerId(typed) && !entries.some((entry) => entry.id === typed);
  const options: { id: string; name: string; note?: string }[] = [
    ...(allowAll && typed === "" ? [{ id: "", name: "All customers" }] : []),
    ...matches,
    ...(offerTyped ? [{ id: typed, name: typed, note: "Use this customer id" }] : []),
  ];

  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const choose = (option: { id: string } | undefined) => {
    if (!option) return;
    onChange(option.id);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Tab") {
      setOpen(false);
      setQuery("");
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(options.length - 1, i + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === "Enter") {
      if (open) {
        event.preventDefault();
        choose(options[active]);
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        setQuery("");
      }
    }
  };

  const activeOption = open ? options[active] : undefined;
  return (
    <div class="combobox" ref={root}>
      <input
        id={id}
        class="input combobox-input"
        type="text"
        role="combobox"
        autocomplete="off"
        spellcheck={false}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeOption ? `${listId}-${active}` : undefined}
        aria-describedby={describedBy}
        aria-invalid={invalid ? "true" : undefined}
        placeholder={placeholder ?? (status === "loading" ? "Loading customers…" : "Search by name or id")}
        value={open ? query : selectedLabel}
        onFocus={(event) => {
          setQuery("");
          setActive(0);
          setOpen(true);
          event.currentTarget.select();
        }}
        onInput={(event) => {
          setQuery(event.currentTarget.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul class="combobox-list" id={listId} role="listbox" aria-label="Customers">
          {options.length === 0 ? (
            <li class="combobox-empty" role="option" aria-disabled="true" aria-selected="false">
              {status === "loading" ? "Loading customers…" : "No customer matches."}
            </li>
          ) : (
            options.map((option, index) => (
              <li
                key={`${option.id}-${index}`}
                id={`${listId}-${index}`}
                class="combobox-option"
                role="option"
                aria-selected={index === active}
                onPointerDown={(event) => {
                  event.preventDefault();
                  choose(option);
                }}
                onPointerEnter={() => setActive(index)}
              >
                <span class="combobox-option-name">{option.name}</span>
                {(option.note || (option.id !== "" && option.id !== option.name)) && (
                  <span class="combobox-option-id">{option.note ?? option.id}</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
